import React, { SyntheticEvent, useEffect, useState } from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material"
import { Row } from "@components/VerticalTable"
import { NodeProperties } from "@features/graph/details/components/PropertiesTable"
import { deepCopy } from "@features/graph/utils/graphUtils"
import { graphReplaceNode } from "@features/graph/state/graphReplaceNode"
import { findNode } from "@utils/graph"
import { GraphStoreType } from "@features/graph/Graph"
import { Node, useStoreApi } from "reactflow"
import { GraphEditMode } from "@features/graph/graphViewModel"
import { EditType } from "@components/EditableCell"
import { ParentChildDetails } from "@features/graph/details/components/ParentChildDetails/ParentChildDetails"
import {
  getExpressionWithCoefficients,
  getUpdatedExpression,
} from "@features/graph/details/components/DeterministicNodeDetails/helpers"
import CoefficientsTable from "@features/graph/details/components/DeterministicNodeDetails/CoefficientsTable"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useLocalStorage } from "usehooks-ts"
import { NodeRowsAccordion } from "@features/graph/details/components/NodeRowsAccordion"
import { NodeDetailsLabel } from "@features/graph/details/components/NodeDetailsLabel"
import { Android12Switch } from "@features/graph/details/components/DeterministicNodeDetails/Switch"
import { useGetModelLagValuesQuery } from "@store/services/modelsService"
import { useGetGraphOriginalLagValueQuery } from "@store/services/graphsService"
import { useGetRunResultQuery } from "@store/services/runService"
import { useParams } from "react-router"
import NodeTagsAccordion from "@features/graph/details/components/NodeTagsAccordion"

type LagNodeDetailsProps = {
  nodeId: string
  editMode: GraphEditMode
  graphKey?: string
  isScenarioDesignPage?: boolean
}

const hasSetValue = (value: string | number | null | undefined) =>
  value !== null && value !== undefined && value !== ""

export const LagNodeDetails = ({
  nodeId,
  editMode,
  graphKey,
  isScenarioDesignPage = false,
}: LagNodeDetailsProps) => {
  const graphStore: GraphStoreType = useStoreApi()
  const [node, setNode] = useState<Node>()
  const [expandedExpression, setExpandedExpression] = useLocalStorage(
    `nodeExpressionAccordionExpanded`,
    true,
  )
  const [showCoefficientsAsVariables, setShowCoefficientsAsVariables] = useState<boolean>(true)

  const { id: runKey } = useParams<{ id: string }>()
  const { data: runData } = useGetRunResultQuery(runKey ?? "", {
    skip: !runKey,
  })
  const modelKey = runData?.model_key ?? ""
  const { data: lagValues } = useGetModelLagValuesQuery(modelKey, {
    skip: !modelKey,
  })

  const hasHistoricMnemonic = !!node?.data.display?.["Historic Mnem"]
  const { data: graphLagValues } = useGetGraphOriginalLagValueQuery(graphKey ?? "", {
    skip: !isScenarioDesignPage || !graphKey || !hasHistoricMnemonic,
  })

  useEffect(() => {
    setNode(findNode(graphStore, nodeId))
  }, [graphStore, nodeId])

  if (!node) {
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  const properties: NodeProperties<any> = node.data.node?.properties ?? {}
  const expression = node.data.node?.properties.df_cpd.expression?.[0] ?? ""
  const { label } = node.data?.display ?? {}
  const historicLagValue = isScenarioDesignPage ? graphLagValues?.[label] : undefined
  const sampleValue =
    [lagValues?.[label], properties.a_sample_value, historicLagValue].find(hasSetValue) ?? null

  const nodeRows: Row[] = [
    { label: "Type", value: "Lag" },
    { label: "Historic Mnemonic", value: node.data.display["Historic Mnem"] },
    { label: "Unit", value: formatValuePythonic("", node.data?.display["Unit Format"]) || "-" },
    { label: "Description", value: node.data?.display["Description"] || "-" },
    { label: "Link", value: node.data.display.Link ?? "" },
    // { label: "Color", children: <ColorPreview color={color} />, value: color },
    { label: "Exclude From Export", value: node.data?.display.exportExcluded ? "yes" : "no" },
  ]

  if (sampleValue !== null) {
    nodeRows.push({
      label: "Sample value",
      value: sampleValue,
      editable: editMode === GraphEditMode.SCENARIO,
      type: EditType.Number,
      onBlur: (newValue) => handlePropertiesFieldCellBlur("a_sample_value", newValue),
    })
  }

  const handlePropertiesFieldCellBlur = async (field: string, newValue: string) => {
    if (!graphKey) {
      return
    }

    node.data.node.properties[field] = Number(newValue)
    await graphReplaceNode(graphStore, graphKey, [node])

    setNode(deepCopy(node))
  }

  const handleCoefficientBlur = async (coefficient: string, newValue: number) => {
    if (!graphKey) {
      return
    }

    const updatedExpression = getUpdatedExpression(expression, coefficient, newValue)
    node.data.node.properties.df_cpd.expression = [updatedExpression]

    await graphReplaceNode(graphStore, graphKey, [node])
    setNode(deepCopy(node))
  }

  const handleToggle = function () {
    if (!graphKey) return
    setShowCoefficientsAsVariables(!showCoefficientsAsVariables)
  }

  // TODO tr: we have no text for lag-mixture, lag-categorical atm AJ to provide
  return (
    <Grid container direction="column" sx={{ p: 1 }} spacing={1}>
      <Grid item>
        <NodeDetailsLabel label={label} />
      </Grid>
      <NodeRowsAccordion nodeRows={nodeRows} />
      <NodeTagsAccordion tags={node.data.display.tags ?? []} />
      {(sampleValue === undefined || sampleValue === "" || sampleValue === null) && (
        <>
          {expression && (
            <>
              <Grid item sx={{ width: expandedExpression ? "auto" : "100%" }}>
                <Accordion
                  expanded={expandedExpression}
                  onChange={(event: SyntheticEvent, isExpanded: boolean) => {
                    setExpandedExpression(isExpanded)
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700 }}>Expression</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <Box sx={{ ml: 1, backgroundColor: "rgba(230, 230, 230, .5)" }}>
                      <FormControlLabel
                        control={<Android12Switch onChange={handleToggle} defaultChecked />}
                        label="Show coefficients as variables"
                      />
                      <Typography>
                        {getExpressionWithCoefficients(node.data).map((token, i) => (
                          <span
                            key={i}
                            style={
                              token.type === "coeff"
                                ? {
                                    background: "rgba(255, 152, 0, 0.4)",
                                    fontWeight: 1000,
                                  }
                                : {}
                            }
                          >
                            {token.value}
                          </span>
                        ))}
                      </Typography>
                    </Box>
                    <CoefficientsTable
                      expression={expression}
                      editable={editMode === GraphEditMode.SCENARIO}
                      onBlur={handleCoefficientBlur}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </>
          )}
        </>
      )}

      <ParentChildDetails />
    </Grid>
  )
}
